/*
 *  Copyright (c) Qwoach LLC 2020. All rights reserved.
 *  Unauthorized copying of this file, via any medium is strictly prohibited
 *  Proprietary and confidential
 *  Created by Artem Bogush <artem@qwoach.com> on 30/9/2020
 */
import BaseObject from "@shared/BaseObject.js";
import {RoleEnum} from "@/core/js/model/Permissions/RoleEnum.js";
import {logError, userTimezone, userTimezoneName} from "@/utils/helpers.js";

export default class AuthManager extends BaseObject {

	auth
	store
	dataManager
	analyticManager
	portalManager = null

	userProfile = null

	constructor(auth, store, dataManager, storageManager) {
		super();
		this.auth = auth
		this.store = store
		this.dataManager = dataManager
		this.storageManager = storageManager
	}

	isLoggedIn(){
		console.log("isLoggedIn: this.auth.currentUser = ", this.auth.currentUser)
		return this.auth.currentUser != null && this.getUserProfile() != null
	}

	getUserProfile(){
		let userProfile = this.store.state.store.userProfile;
		console.log("AuthManager.getUserProfile = ", userProfile)
		return userProfile;
	}
	getCoachProfile(){
		let coachProfile = this.store.state.store.coachProfile;
		console.log("AuthManager.getCoachProfile = ", coachProfile)
		return coachProfile;
	}
	getOwnerProfile(){
		let ownerProfile = this.store.state.store.ownerProfile;
		console.log("AuthManager.getOwnerProfile = ", ownerProfile)
		return ownerProfile;
	}

	getCurrentUser(){
		let currentUser = this.store.state.store.currentUser;
		console.log("AuthManager.getCurrentUser = ", currentUser)
		return currentUser;
	}

	isQwoachV2App(){
		let ownerProfile = this.getOwnerProfile()
		return ownerProfile && ownerProfile.appVersion === 2
	}

	getUserRole(){
		return this.isLoggedIn() ? this.getUserProfile().role : null;
	}

	isOwner(){
		let userProfile = this.getUserProfile()
		if(userProfile){
			return userProfile.isOwner()
		}
		return false
	}
	isCoach(){
		return this.getUserRole() === RoleEnum.COACH
	}

	isClient(){
		return this.getUserRole() === RoleEnum.CLIENT
	}

	getCoachId(){
		let ownerProfile = this.getOwnerProfile()
		return ownerProfile ? ownerProfile.id : null
	}

	getOwnerId(){
		let ownerProfile = this.getOwnerProfile()
		return ownerProfile ? ownerProfile.id : null
	}



	async logout() {
		let loginPathName = this.getLoginPathName()
		await this.auth.signOut()
		await this.store.dispatch('clearData')
		return loginPathName
	}

	getLoginPathName(){
		if(this.isCoach()){
			return "LoginCoach"
		}
		return "Login"
	}

	async clientSignup(password, userProfile){
		console.log("AuthManager.clientSignup start")
		if(!userProfile.email){
			throw new Error("clientSignup: missing required filed userProfile.email")
		}
		userProfile = Object.assign({}, userProfile)
		userProfile.password = password
		userProfile = await this.dataManager.apiPostCreateClient(userProfile)
		return await this.login(userProfile.email, password, true)
	}

	async refreshUserToken(){
		if(this.auth.currentUser) {
			await this.auth.currentUser.getIdToken(true)
			await this.fetchAndSyncUserProfile()
		}
	}

	async getUserByEmail(email){
		return await this.auth.getUserByEmail(email)
	}

	async waitForCustomClaimsFor(ownerId){
		return new Promise((resolve, reject) => {
			let stopPromise = this.dataManager.onSnapshotCustomClaimsDoc(async (doc, error) => {
				console.log("AuthManager.waitForCustomClaimsFor = ", doc, error)
				if(error){
					let stop = await stopPromise
					stop()
					reject(error)
					return
				}
				await this.refreshUserToken()
				let userProfile = this.getUserProfile()
				console.log("AuthManager.waitForCustomClaimsFor: userProfile = ", userProfile, ", ownerId = ", ownerId)
				if(userProfile && userProfile.hasAccessRulesFor(ownerId)){
					let stop = await stopPromise
					stop()
					resolve()
				}
			})
		})
	}

	async startListenForClaimsChange(){
		console.log("AuthManager.startListenForClaimsChange")
		if(this.isListeningForClaimsChange){
			return
		}
		this.isListeningForClaimsChange = true
		await this.dataManager.onSnapshotCustomClaimsDoc((doc, error) => {
			console.log("AuthManager.onSnapshotCustomClaimsDoc = ", doc, error)
			if(error){
				logError(error)
				this.isListeningForClaimsChange = false
				if(error.code !== "permission-denied"){
					setTimeout(() => {
						this.startListenForClaimsChange()
					}, 10000)
				}
			} else {
				this.refreshUserToken()
			}
		})
	}

	async memberSignup(password, userProfile){
		console.log("AuthManager.memberSignup start")
		if(!userProfile.email){
			throw new Error("memberSignup: missing required filed userProfile.email")
		}
		userProfile = Object.assign({}, userProfile)
		userProfile.password = password
		userProfile = await this.dataManager.apiPostCreateTeamMemberAccount(userProfile)
		return await this.login(userProfile.email, password, true)
	}


	async login(email, password, isUserCreated){
		console.log("Method Start: AuthManager.login: ", email)
		let {user} = await this.auth.signInWithEmailAndPassword(email, password)
		console.log("AuthManager.signInWithEmailAndPassword: user = ", user)
		return await this.onUserLoggedIn(user, isUserCreated)
	}


	async logUserLoggedIn(userProfile, isUserCreated){
		this.analyticManager.setUserId(userProfile.id, isUserCreated)
		this.analyticManager.setUserEmail(userProfile.email)
		await this.analyticManager.setUserProps({
			role: userProfile.role
		})
	}


	async onUserLoggedIn(user, isUserCreated){
		let [userProfile, userSubscription] = await Promise.all([
			this.dataManager.getUserProfile(user.uid),
			this.dataManager.apiGetUserSubscription({userId: user.uid})
		])
		this.dataManager.currentUserId = userProfile.id
		this.storageManager.currentUserId = userProfile.id

		await this.ensureTimeZone(userProfile)

		// this.dataManagerMe.currentUserId = userProfile.id
		// this.dataManagerMe.ownerId = userProfile.id

		await this.setDefaultPortal(userProfile)


		console.log("AuthManager.onUserLoggedIn")
		this.setUserSubscription(userSubscription)
		this.store.commit('setCurrentUser', user)
		this.store.commit('setUserProfile', userProfile)
		await this.logUserLoggedIn(userProfile, isUserCreated)
		await this.startListenForClaimsChange()
		return userProfile
	}

	async ensureTimeZone(userProfile){
		let timezoneId = userTimezone()
		console.log("ensureTimeZone: timezoneId = ", timezoneId, ", userProfile = ", userProfile)
		if(userProfile.getTimeZoneId() !== timezoneId){
			let timezoneName = userTimezoneName(timezoneId)
			await this.dataManager.apiPostSetUserTimezone({userProfile, timezoneId, timezoneName})
		}
	}

	async setDefaultPortal(userProfile){
		await this.portalManager.setDefaultPortal(userProfile)
	}

	async fetchAndSyncUserProfile(){
		console.log("fetchAndSyncUserProfile")
		let userProfile = this.getUserProfile()
		if(userProfile){
			let userProfileNew = await this.dataManager.getUserProfile(userProfile.id)
			await this.setDefaultPortal(userProfileNew)

			console.log("fetchAndSyncUserProfile: userProfileNew = ", userProfileNew)
			this.store.commit('setUserProfile', userProfileNew)
		}
	}

	async updateUserProfile(userProfile){
		await this.dataManager.setUserProfile(userProfile)
		this.store.commit('setUserProfile', userProfile)
		if(userProfile.isOwner()){
			if(userProfile.isCoach()){
				this.store.commit('setCoachProfile', userProfile)
			}
			this.store.commit('setOwnerProfile', userProfile)
		}
	}

	setUserSubscription(userSubscription){
		this.store.commit('setUserSubscription', userSubscription)
	}

	async setCurrentCoachProfile(userProfile){
		let coachProfile = userProfile
		let ownerProfile = userProfile
		let ownerSubscription = null
		let ownerId = userProfile.getOwnerId()
		console.log("AuthManager.setCurrentCoachProfile: userProfile = ", userProfile, ", ownerId = ", ownerId)
		if(!userProfile.isOwner()){
			if(userProfile.isClient()){
				if(userProfile.currentCoachId){
					coachProfile = await this.dataManager.apiGetCurrentCoachProfile({coachId: userProfile.currentCoachId})
				} else {
					coachProfile = await this.dataManager.getUserProfile(ownerId)
				}
			}
			ownerProfile = await this.dataManager.getUserProfile(ownerId)
			ownerSubscription = await this.dataManager.apiGetUserSubscription({userId: ownerId})
		}
		this.dataManager.ownerId = ownerId
		this.storageManager.ownerId = ownerId

		//TODO legacy code support - needs to be removed after refactoring
		this.server.fbStorage.coachId = ownerId
		this.server.paths.coachId = ownerId

		console.log("setCurrentCoachProfile: coachProfile = ", coachProfile)
		this.store.commit('setCoachProfile', coachProfile)
		this.store.commit('setOwnerProfile', ownerProfile)
		this.store.commit('setOwnerSubscription', ownerSubscription)

		return coachProfile
	}


	async waitForUserSubscription(options = {}){

		return new Promise((resolve, reject) => {
			let {
				paymentStartTs = 0,
				predicate,
				userId = this.getCoachId(),
			} = options

			let stopSnapshotPromise = this.dataManager.onSnapshotUserSubscription(userId, async (subscription, error) => {
				console.log("onSnapshotUserSubscription: ", subscription, ", userSubscription = ", this.userSubscription, ", error = ", error)

				if (error) {
					this.handleError(error)
					let stopSnapshot = await stopSnapshotPromise
					stopSnapshot()
					reject(error)
					return
				}
				console.log("onSnapshotUserSubscription: subscription = ", subscription)

				let isNewSubscription = subscription?.updatedAt >= paymentStartTs

				let defaultPredicate = (subscription) => subscription.isStarted() || subscription.isPending()
				predicate = predicate || defaultPredicate

				if (subscription && isNewSubscription && predicate(subscription)) {
					this.setUserSubscription(subscription)
					let stopSnapshot = await stopSnapshotPromise
					stopSnapshot()
					resolve(subscription)
				}
			})

		})

	}




	startListenForLoginStateChange(callback = () => {}) {
		console.log("AuthManager.startListenForLoginStateChange")
		return this.auth.onAuthStateChanged(async user => {
			console.log("AuthManager.onAuthStateChanged: user = ", user)
			if (user) {
				if(this.userProfile && this.userProfile.id === user.uid){
					console.log("AuthManager.onAuthStateChanged: user already logged in")
					callback(this.userProfile)
					return
				}
				try{
					let userProfile = await this.onUserLoggedIn(user)
					this.userProfile = userProfile
					callback(userProfile)
				} catch (error){
					this.userProfile = null
					callback(null, error)
				}
			} else {
				this.store.commit('setCurrentUser', null)
				this.userProfile = null
				callback(null)
			}
		}, error =>{
			console.error("onAuthStateChanged: error = ", error)
			this.userProfile = null
			callback(null, error)
		})
	}


}
